import { ThemeEvents, CartAddEvent } from '@theme/events';

/**
 * "Completa tu look" — cart cross-sell widget.
 * Fetches recommendations based on the first cart item and allows quick add.
 */
class CartCompleteLook extends HTMLElement {
  #listEl;
  #cartItemIds = new Set();
  #seedProductId = '';
  #limit = 4;
  #intent = 'complementary';
  #route = '/recommendations/products';
  #labels = { add: 'Añadir', adding: 'Añadiendo...', added: 'Añadido', error: 'Error' };
  #boundOnCartUpdate;
  #abortController = null;
  #requestId = 0;
  #requestTimeout = 7000;

  connectedCallback() {
    this.#listEl = this.querySelector('[data-ccl-list]');
    this.#seedProductId = String(this.dataset.seedProductId || '');
    this.#limit = Number(this.dataset.limit || 4);
    this.#intent = this.dataset.intent || 'complementary';
    this.#route = this.dataset.route || '/recommendations/products';
    this.#labels = {
      add: this.dataset.addLabel || this.#labels.add,
      adding: this.dataset.addingLabel || this.#labels.adding,
      added: this.dataset.addedLabel || this.#labels.added,
      error: this.dataset.errorLabel || this.#labels.error,
    };

    this.#boundOnCartUpdate = this.#onCartUpdate.bind(this);
    document.addEventListener(ThemeEvents.cartUpdate, this.#boundOnCartUpdate);

    this.#loadCartState().finally(() => this.#fetchRecommendations());
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#boundOnCartUpdate);
    this.#abortController?.abort();
  }

  async #onCartUpdate(event) {
    const cart = event.detail?.resource;
    if (cart && Array.isArray(cart.items)) {
      this.#cartItemIds = new Set(cart.items.map((item) => String(item.product_id)));
      // If the seed product is no longer in cart, switch seed to the new first item
      const firstItem = cart.items[0];
      if (firstItem) {
        const firstPid = String(firstItem.product_id);
        if (this.#seedProductId !== firstPid) {
          this.#seedProductId = firstPid;
          this.#fetchRecommendations();
          return;
        }
      } else {
        this.classList.add('is-hidden');
        return;
      }
      this.#syncInCartButtons();
    } else {
      await this.#loadCartState();
      this.#syncInCartButtons();
    }
  }

  async #loadCartState() {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const res = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      const data = await res.json();
      this.#cartItemIds = new Set((data.items || []).map((item) => String(item.product_id)));
    } catch {
      this.#cartItemIds = new Set();
    }
  }

  async #fetchRecommendations() {
    const requestId = ++this.#requestId;

    if (!this.#seedProductId) {
      this.#useFallback(requestId);
      return;
    }
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const intents = this.#intent === 'related'
      ? ['related', 'complementary']
      : ['complementary', 'related'];

    let products = [];
    for (const intent of intents) {
      try {
        products = await this.#fetchIntent(intent);
        if (products.length) break;
      } catch (err) {
        if (err?.name === 'AbortError') return;
      }
    }

    if (!this.#isCurrentRequest(requestId)) return;

    if (!products.length) {
      this.#useFallback(requestId);
      return;
    }
    this.#render(products);
  }

  /**
   * @param {string} intent
   * @returns {Promise<Array<any>>}
   */
  async #fetchIntent(intent) {
    const basePath = (this.#route || '/recommendations/products').replace(/\.json$/, '').replace(/\/$/, '');
    const params = new URLSearchParams({
      product_id: String(this.#seedProductId),
      limit: String(this.#limit),
      intent,
    });
    const res = fetch(`${basePath}.json?${params.toString()}`, {
      headers: { Accept: 'application/json' },
      signal: this.#abortController.signal,
    });
    const timedRes = await this.#withTimeout(res);
    if (!timedRes) return [];
    if (!timedRes.ok) return [];
    const data = await timedRes.json();
    return (data.products || []).filter((p) => !this.#cartItemIds.has(String(p.id)));
  }

  #useFallback(requestId = this.#requestId) {
    // 1. Embedded fallback collection (rendered server-side in Liquid)
    const fallbackEl = this.querySelector('[data-ccl-fallback]');
    if (fallbackEl) {
      try {
        const list = JSON.parse(fallbackEl.textContent || '[]');
        const filtered = list
          .filter((p) => !this.#cartItemIds.has(String(p.id)))
          .slice(0, this.#limit);
        if (filtered.length) {
          if (!this.#isCurrentRequest(requestId)) return;
          this.#render(filtered);
          return;
        }
      } catch {
        // ignore parse errors
      }
    }
    // 2. Fetch fallback collection client-side if handle provided but not embedded
    const handle = this.dataset.fallbackCollection;
    if (handle) {
      this.#fetchCollection(handle).then((products) => {
        if (!this.#isCurrentRequest(requestId)) return;
        const filtered = products
          .filter((p) => !this.#cartItemIds.has(String(p.id)))
          .slice(0, this.#limit);
        if (filtered.length) this.#render(filtered);
        else this.#hide(requestId);
      }).catch(() => this.#hide(requestId));
      return;
    }
    this.#hide(requestId);
  }

  /**
   * @param {string} handle
   * @returns {Promise<Array<any>>}
   */
  async #fetchCollection(handle) {
    const root = window.Shopify?.routes?.root || '/';
    const res = fetch(`${root}collections/${handle}/products.json?limit=12`, {
      headers: { Accept: 'application/json' },
      signal: this.#abortController?.signal,
    });
    const timedRes = await this.#withTimeout(res);
    if (!timedRes || !timedRes.ok) return [];
    const data = await timedRes.json();
    return data.products || [];
  }

  /**
   * @param {Promise<Response>} request
   * @returns {Promise<Response | null>}
   */
  async #withTimeout(request) {
    let timeoutId;
    const timeout = new Promise((resolve) => {
      timeoutId = window.setTimeout(() => resolve(null), this.#requestTimeout);
    });
    try {
      return await Promise.race([request, timeout]);
    } finally {
      window.clearTimeout(timeoutId);
    }
  }

  #isCurrentRequest(requestId) {
    return this.isConnected && requestId === this.#requestId;
  }

  #hide(requestId = this.#requestId) {
    if (!this.#isCurrentRequest(requestId)) return;
    this.classList.add('is-hidden');
  }

  /**
   * @param {Array<any>} products
   */
  #render(products) {
    if (!this.#listEl) return;
    if (!products.length) {
      this.classList.add('is-hidden');
      return;
    }
    this.classList.remove('is-hidden');

    this.#listEl.replaceChildren(
      ...products.map((p) => this.#renderCard(p))
    );
  }

  /**
   * @param {any} product
   * @returns {HTMLElement}
   */
  #renderCard(product) {
    const card = document.createElement('div');
    card.className = 'cart-complete-look__card';
    card.dataset.productId = String(product.id);

    const imgSrc = this.#getImageSrc(product);
    const priceHtml = this.#renderPriceHtml(product);
    const url = product.url || (product.handle ? `${(window.Shopify?.routes?.root || '/')}products/${product.handle}` : '#');
    const title = product.title || '';
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const firstAvailableVariant = variants.find((v) => v.available) || variants[0];
    const hasVariants = variants.length > 1;

    card.innerHTML = `
      <a class="cart-complete-look__image-link" href="${this.#escape(url)}" aria-label="${this.#escape(title)}">
        ${imgSrc ? `<img class="cart-complete-look__image" src="${this.#escape(imgSrc)}" alt="${this.#escape(title)}" loading="lazy" onerror="this.style.display='none'">` : ''}
      </a>
      <a class="cart-complete-look__title-link" href="${this.#escape(url)}">${this.#escape(title)}</a>
      <div class="cart-complete-look__price">${priceHtml}</div>
      <button
        type="button"
        class="button button-secondary cart-complete-look__button"
        data-ccl-add
        ${!firstAvailableVariant || !firstAvailableVariant.available ? 'disabled' : ''}
        data-variant-id="${firstAvailableVariant ? firstAvailableVariant.id : ''}"
        data-product-url="${this.#escape(url)}"
        data-has-variants="${hasVariants ? '1' : '0'}"
      >
        <span class="button-text">${!firstAvailableVariant || !firstAvailableVariant.available ? 'Agotado' : this.#escape(this.#labels.add)}</span>
      </button>
    `;

    const btn = card.querySelector('[data-ccl-add]');
    btn?.addEventListener('click', (e) => this.#onAdd(e, product));
    return card;
  }

  /**
   * @param {any} product
   */
  #getImageSrc(product) {
    const pickStr = (v) => {
      if (typeof v === 'string' && v) return v;
      if (v && typeof v === 'object' && typeof v.src === 'string') return v.src;
      return '';
    };
    let src =
      pickStr(product.featured_image) ||
      pickStr(product.image) ||
      (Array.isArray(product.images) ? pickStr(product.images[0]) : '');
    if (!src) return '';
    if (src.startsWith('//')) src = 'https:' + src;
    return src;
  }

  /**
   * @param {any} product
   */
  #renderPriceHtml(product) {
    const toCents = (v) => {
      if (v == null) return 0;
      if (typeof v === 'number') return v < 1000 ? Math.round(v * 100) : v; // sometimes price comes in major units
      if (typeof v === 'string') {
        const n = parseFloat(v);
        if (!isFinite(n)) return 0;
        return v.includes('.') && n < 1000 ? Math.round(n * 100) : Math.round(n);
      }
      return 0;
    };
    const format = (cents) => {
      if (typeof window.Shopify?.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents);
      }
      return (cents / 100).toFixed(2).replace('.', ',') + ' €';
    };
    const priceCents = toCents(product.price);
    const compareCents = toCents(product.compare_at_price_max ?? product.compare_at_price);
    const price = format(priceCents);
    if (compareCents && compareCents > priceCents) {
      return `${price} <span class="money-compare">${format(compareCents)}</span>`;
    }
    return price;
  }

  /**
   * @param {MouseEvent} event
   * @param {any} product
   */
  async #onAdd(event, product) {
    const btn = /** @type {HTMLButtonElement} */ (event.currentTarget);
    const variantId = btn.dataset.variantId;
    const hasVariants = btn.dataset.hasVariants === '1';
    const productUrl = btn.dataset.productUrl;

    // If the product has multiple variants, redirect to the product page to pick one.
    if (hasVariants && productUrl) {
      window.location.href = productUrl;
      return;
    }
    if (!variantId) return;

    const originalLabel = btn.querySelector('span')?.textContent || this.#labels.add;
    btn.disabled = true;
    const span = btn.querySelector('span');
    if (span) span.textContent = this.#labels.adding;

    try {
      const root = window.Shopify?.routes?.root || '/';
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      const addRes = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
      });
      if (!addRes.ok) throw new Error('Add failed');

      const cartRes = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await cartRes.json();

      document.dispatchEvent(
        new CartAddEvent(cart, this.id || 'cart-complete-look', {
          source: 'cart-complete-look',
          productId: String(product.id),
          variantId,
          itemCount: cart.item_count,
        })
      );

      if (span) span.textContent = this.#labels.added;
      setTimeout(() => {
        if (span) span.textContent = originalLabel;
        btn.disabled = false;
      }, 1500);
    } catch {
      if (span) span.textContent = this.#labels.error;
      setTimeout(() => {
        if (span) span.textContent = originalLabel;
        btn.disabled = false;
      }, 1500);
    }
  }

  #syncInCartButtons() {
    this.querySelectorAll('.cart-complete-look__card').forEach((card) => {
      const pid = card.dataset.productId;
      if (pid && this.#cartItemIds.has(String(pid))) {
        card.remove();
      }
    });
    if (this.#listEl && this.#listEl.children.length === 0) {
      this.classList.add('is-hidden');
    }
  }

  #escape(str) {
    return String(str ?? '').replace(/[&<>"']/g, (c) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    })[c]);
  }
}

if (!customElements.get('cart-complete-look')) {
  customElements.define('cart-complete-look', CartCompleteLook);
}
